import type { TranslationKey } from "@/utils/type-utils";
import { Box, Chip, ListItem, Typography } from "@mui/material";
import type { FilterOption } from "@st-marketplace/ui";
import type { HTMLAttributes } from "react";
import type { SelectItem } from "./SelectField";

export interface FacetOption<TValue extends string | number = string> {
	value: TValue;
	label: string | TranslationKey;
	total: number;
	filtered: number;
}

interface FacetMenuItemProps<TValue extends string | number> {
	props?: HTMLAttributes<HTMLLIElement>;
	option: FacetOption<TValue>;
}

const FacetMenuItem = <TValue extends string | number>({
	props,
	option,
}: FacetMenuItemProps<TValue>) => {
	return (
		<ListItem {...props} disableGutters sx={{ p: 0 }}>
			<Box
				sx={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 2,
				}}
			>
				<Typography variant="body2">{option.label}</Typography>

				<Chip
					size="small"
					color={option.filtered === 0 ? "default" : "primary"}
					label={`${option.filtered}/${option.total}`}
				/>
			</Box>
		</ListItem>
	);
};

export default FacetMenuItem;

interface FacetCount<T extends string = string> {
	value: T | null;
	total: number;
	filtered: number;
}

export function mapFacetCountsToOptions<T extends string>(
	facets?: FacetCount<T>[] | null,
	labelMap?: (value: T) => string,
): FacetOption<T>[] {
	if (!facets) return [];

	return facets
		.filter((f): f is FacetCount<T> & { value: T } => f.value != null)
		.map((f) => ({
			value: f.value,
			label: labelMap ? labelMap(f.value) : String(f.value),
			total: f.total,
			filtered: f.filtered,
		}));
}

/**
 * Renders a facet counter as an emphasized `filtered` over a muted `total`.
 * The filtered count is the number the user is actually choosing between, so
 * the denominator stays quiet.
 *
 * `FilterOption.counter` is typed as a string, but the design system renders it
 * as a plain child, so a node works. The cast is contained here.
 */
export function facetCounter(filtered: number, total: number): string {
	return (
		<>
			<span className="font-semibold text-secondary-text">{filtered}</span>
			<span className="text-secondary-text-disabled">/{total}</span>
		</>
	) as unknown as string;
}

/** Facet counts as ready-to-use `GenericFilters` options. */
export function toFacetFilterOptions<T extends string>(
	facets?: FacetCount<T>[] | null,
	labelMap?: (value: T) => string,
): FilterOption<T>[] {
	return mapFacetCountsToOptions(facets, labelMap).map((option) => ({
		value: option.value,
		label: String(option.label),
		counter: facetCounter(option.filtered, option.total),
	}));
}

export type FacetMeta = {
	total: number;
	filtered: number;
};

export function mapFacetOptionsToSelectItems<T extends string>(
	options: FacetOption<T>[],
): SelectItem<T, FacetMeta>[] {
	return options.map(({ value, label, total, filtered }) => ({
		value,
		title: label as string,
		meta: { total, filtered },
	}));
}
